import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dec, type Dec } from '@praticheenergia/ad1-engine'
import {
  Alert, Box, Button, Chip, InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { caricaSogliaPartner, elencaImpiantiGreen, salvaSogliaImpianto, salvaSogliaPartner } from './green.api'
import { etichettaMese, kwh, percento } from './formato'
import { valutaScostamento, type StatoScostamento } from './scostamento'

const ETICHETTE: Record<StatoScostamento, { testo: string; colore: 'success' | 'error' | 'default' }> = {
  ok: { testo: 'Nella norma', colore: 'success' },
  anomalia: { testo: 'Anomalia', colore: 'error' },
  senza_storico: { testo: 'Senza storico', colore: 'default' },
  senza_dati: { testo: 'Senza dati', colore: 'default' },
}

const sogliaValida = (v: string) => /^\d+([.,]\d+)?$/.test(v) && Number(v.replace(',', '.')) > 0 && Number(v.replace(',', '.')) <= 100

export function Controllo() {
  const idPartner = Number(useParams().idPartner)
  const client = useQueryClient()
  const { data: impianti = [], error } = useQuery({ queryKey: ['impianti-green'], queryFn: elencaImpiantiGreen })
  const { data: predefinita = '20' } = useQuery({ queryKey: ['soglia-partner', idPartner], queryFn: () => caricaSogliaPartner(idPartner) })

  const [bozza, setBozza] = useState(predefinita)
  useEffect(() => setBozza(predefinita), [predefinita])

  const salvaPredefinita = useMutation({
    mutationFn: (v: string) => salvaSogliaPartner(idPartner, v.replace(',', '.')),
    onSuccess: () => client.invalidateQueries({ queryKey: ['soglia-partner', idPartner] }),
  })
  const salvaPerImpianto = useMutation({
    mutationFn: ({ id, v }: { id: number; v: string | null }) => salvaSogliaImpianto(id, v),
    onSuccess: () => client.invalidateQueries({ queryKey: ['impianti-green'] }),
  })

  const righe = impianti
    .map((i) => ({ ...i, valutazione: valutaScostamento(i.serie, i.soglia ?? predefinita) }))
    // Le anomalie in testa: e' la lista di lavoro, non un elenco alfabetico.
    .sort((a, b) => Number(b.valutazione.stato === 'anomalia') - Number(a.valutazione.stato === 'anomalia'))
  const anomalie = righe.filter((r) => r.valutazione.stato === 'anomalia').length

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h5" component="h1">Controllo</Typography>
        <Typography color="text.secondary">
          Produzione dell'ultimo periodo confrontata con lo stesso periodo dell'anno precedente.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error.message}</Alert>}
      {salvaPredefinita.error && <Alert severity="error">{salvaPredefinita.error.message}</Alert>}
      {salvaPerImpianto.error && <Alert severity="error">{salvaPerImpianto.error.message}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
          <TextField
            label="Soglia di scostamento predefinita"
            size="small"
            value={bozza}
            onChange={(e) => setBozza(e.target.value)}
            error={!sogliaValida(bozza)}
            helperText={sogliaValida(bozza) ? 'Vale per tutti gli impianti senza una soglia propria' : 'Fra 0 e 100'}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            sx={{ maxWidth: 320 }}
          />
          <Button variant="contained" disabled={!sogliaValida(bozza) || bozza === predefinita || salvaPredefinita.isPending}
            onClick={() => salvaPredefinita.mutate(bozza)}>
            Salva
          </Button>
          <Box sx={{ flex: 1 }} />
          <Chip color={anomalie > 0 ? 'error' : 'success'} label={anomalie === 1 ? '1 impianto in anomalia' : `${anomalie} impianti in anomalia`} />
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" aria-label="Controllo della produzione">
            <TableHead>
              <TableRow>
                <TableCell>Ragione sociale</TableCell>
                <TableCell>Codice impianto</TableCell>
                <TableCell>Comune</TableCell>
                <TableCell>Ultimo mese scaricato</TableCell>
                <TableCell align="right">Produzione ultimi 12 mesi</TableCell>
                <TableCell align="right">Ultimo mese su anno prima</TableCell>
                <TableCell align="right">12 mesi su anno prima</TableCell>
                <TableCell>Soglia</TableCell>
                <TableCell>Esito</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {righe.map(({ valutazione: v, ...i }) => (
                <TableRow key={i.id} sx={v.stato === 'anomalia' ? { bgcolor: '#fef2f2' } : {}}>
                  <TableCell>{i.ragioneSociale}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.codiceImpianto}</TableCell>
                  <TableCell>{i.comune}</TableCell>
                  <TableCell>{v.ultimoMese ? etichettaMese(v.ultimoMese.anno, v.ultimoMese.mese) : '—'}</TableCell>
                  <TableCell align="right">{kwh(v.produzione12Mesi)}</TableCell>
                  <Variazione valore={v.variazioneMese} soglia={i.soglia ?? predefinita} />
                  <Variazione valore={v.variazione} soglia={i.soglia ?? predefinita} />
                  <TableCell>
                    <SogliaImpianto soglia={i.soglia} predefinita={predefinita} etichetta={`Soglia di ${i.codiceImpianto}`}
                      onSalva={(valore) => salvaPerImpianto.mutate({ id: i.id, v: valore })} />
                  </TableCell>
                  <TableCell><Chip size="small" color={ETICHETTE[v.stato].colore} label={ETICHETTE[v.stato].testo} /></TableCell>
                </TableRow>
              ))}
              {righe.length === 0 && (
                <TableRow><TableCell colSpan={9}><Typography color="text.secondary" sx={{ py: 2 }}>Nessun impianto nel Controllo consumi.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  )
}

/** In rosso la variazione che da sola fa scattare l'anomalia. */
function Variazione({ valore, soglia }: { valore: Dec | null; soglia: string }) {
  const oltre = valore !== null && valore.lessThan(dec(soglia).negated())
  return (
    <TableCell align="right" sx={oltre ? { color: 'error.main', fontWeight: 700 } : {}}>
      {percento(valore)}
    </TableCell>
  )
}

/** Vuota: vale la predefinita. Si salva all'uscita dal campo, solo se cambiata e valida. */
function SogliaImpianto({ soglia, predefinita, etichetta, onSalva }: {
  soglia: string | null; predefinita: string; etichetta: string; onSalva: (v: string | null) => void
}) {
  const [valore, setValore] = useState(soglia ?? '')
  useEffect(() => setValore(soglia ?? ''), [soglia])
  const valido = valore === '' || sogliaValida(valore)
  return (
    <TextField
      size="small"
      value={valore}
      placeholder={predefinita}
      onChange={(e) => setValore(e.target.value)}
      onBlur={() => {
        const nuovo = valore === '' ? null : valore.replace(',', '.')
        if (valido && nuovo !== soglia) onSalva(nuovo)
      }}
      error={!valido}
      slotProps={{ htmlInput: { 'aria-label': etichetta }, input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
      sx={{ width: 96 }}
    />
  )
}
